import { DialogTrigger } from 'react-aria-components';

import { Button, Popup, Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import { useUpdateDiscount } from '../api/useUpdateDiscount';

/** One year in milliseconds */
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

interface ActivateDiscountDialogProps {
	discountId: string;
	discountName: string;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation dialog for activating an expired discount.
 * Activation extends the discount's endsAt to one year from now, restoring "Active" status.
 */
export const ActivateDiscountDialog = ({
	discountId,
	discountName,
	isOpen,
	onOpenChange,
}: ActivateDiscountDialogProps) => {
	const { mutate: updateDiscount, isPending } = useUpdateDiscount();

	const handleActivate = () => {
		const now = Date.now();
		updateDiscount(
			{
				discountId,
				payload: {
					startsAt: now,
					endsAt: now + ONE_YEAR_MS,
				},
			},
			{
				onSuccess: () => onOpenChange(false),
				onError: () => {
					toast({ message: 'Failed to activate discount. Please try again.', variant: 'error' });
				},
			},
		);
	};

	return (
		<DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
			<Popup title='Activate discount?'>
				<Stack padding='0' itemsSpacing='24'>
					<Text size='body' color='secondary'>
						Activating <strong>{discountName}</strong> will allow it to apply to new
						purchases. The discount will be active for one year from today.
					</Text>
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: '12px',
							paddingTop: '8px',
						}}>
						<Button
							variant='secondary'
							size='medium'
							onPress={() => onOpenChange(false)}
							isDisabled={isPending}>
							Cancel
						</Button>
						<Button
							variant='filled'
							size='medium'
							onPress={handleActivate}
							isLoading={isPending}>
							Activate
						</Button>
					</div>
				</Stack>
			</Popup>
		</DialogTrigger>
	);
};
